import type { Translations } from '@/i18n/locales/en';
import type { CustomerOption, CustomerProduct } from '@restaurant/api-contracts';
import { canMeetMinimum, effectiveSelectBounds } from '@restaurant/types';

type ConfigureItemStrings = Translations['tableOrdering']['configureItem'];

/** One selected option choice on a draft line (kept denormalised for display). */
export interface DraftChoice {
  optionId: string;
  optionChoiceId: string;
  label: string;
  priceCents: number;
}

/** A line in the round being built: a product + chosen options + quantity,
 *  optionally tagged to a seat (null = Shared). */
export interface DraftLine {
  key: string;
  product: CustomerProduct;
  quantity: number;
  choices: DraftChoice[];
  seatId: string | null;
}

/** Pre-select an option's defaults (or a required single-select's first choice).
 *  Pass `existing` to seed from a line already in the round (edit). */
export function initialSelections(
  p: CustomerProduct,
  existing?: DraftChoice[],
): Record<string, string[]> {
  const sel: Record<string, string[]> = {};
  for (const opt of p.options) {
    if (existing) {
      sel[opt.id] = existing.filter((c) => c.optionId === opt.id).map((c) => c.optionChoiceId);
      continue;
    }
    const defaults = opt.choices.filter((c) => c.isDefault && c.available).map((c) => c.id);
    if (defaults.length > 0) {
      sel[opt.id] = opt.isMultiple ? defaults : [defaults[0]];
    } else if (opt.isRequired && !opt.isMultiple) {
      const first = opt.choices.find((c) => c.available);
      sel[opt.id] = first ? [first.id] : [];
    } else {
      sel[opt.id] = [];
    }
  }
  return sel;
}

export function selectionsToChoices(
  p: CustomerProduct,
  selections: Record<string, string[]>,
): DraftChoice[] {
  const out: DraftChoice[] = [];
  for (const opt of p.options) {
    for (const choiceId of selections[opt.id] ?? []) {
      const c = opt.choices.find((x) => x.id === choiceId);
      if (c) {
        out.push({
          optionId: opt.id,
          optionChoiceId: c.id,
          label: c.name,
          priceCents: c.price,
        });
      }
    }
  }
  return out;
}

export function defaultChoices(p: CustomerProduct): DraftChoice[] {
  return selectionsToChoices(p, initialSelections(p));
}

/** Identity of a line = product + its sorted chosen choices + seat, so the same
 *  dish with the same options merges per seat, but a different seat (or options)
 *  stays a separate line. */
export function lineKey(productId: string, choices: DraftChoice[], seatId: string | null): string {
  return [productId, seatId ?? 'shared', ...choices.map((c) => c.optionChoiceId).sort()].join(':');
}

export function availableChoiceCount(opt: CustomerOption): number {
  return opt.choices.filter((c) => c.available).length;
}

export function isOptionFull(opt: CustomerOption, pickedCount: number): boolean {
  const { max } = effectiveSelectBounds(opt);
  return opt.isMultiple && max != null && pickedCount >= max;
}

/** First unmet requirement, or null when valid. Drives the disabled Add button
 *  + an inline hint - no error popups. */
export function configError(
  p: CustomerProduct,
  selections: Record<string, string[]>,
  t: ConfigureItemStrings,
): string | null {
  for (const opt of p.options) {
    if (!canMeetMinimum(opt, availableChoiceCount(opt))) return t.temporarilyUnavailable;
  }
  for (const opt of p.options) {
    const n = (selections[opt.id] ?? []).length;
    const { min } = effectiveSelectBounds(opt);
    if (n < min) return t.pickMore(min - n, opt.name);
  }
  return null;
}

export function optionShortage(opt: CustomerOption, t: ConfigureItemStrings): string | null {
  if (canMeetMinimum(opt, availableChoiceCount(opt))) return null;
  return t.optionShortage(
    opt.name,
    availableChoiceCount(opt),
    opt.choices.length,
    effectiveSelectBounds(opt).min,
  );
}

export function optionHint(
  opt: CustomerOption,
  pickedCount: number,
  t: ConfigureItemStrings,
): string {
  if (!opt.isMultiple) return t.chooseOne;
  const { min, max } = effectiveSelectBounds(opt);
  if (max == null) return min > 0 ? t.atLeast(min, pickedCount) : t.chooseAny;
  if (min === max) return t.chooseExact(max, pickedCount);
  if (min === 0) return t.upTo(max, pickedCount);
  return t.chooseRange(min, max, pickedCount);
}

export function optionRequired(opt: CustomerOption): boolean {
  return effectiveSelectBounds(opt).min > 0;
}

export function lineUnit(l: DraftLine): number {
  return l.product.price + l.choices.reduce((s, c) => s + c.priceCents, 0);
}
